import React from 'react';
import PropTypes from 'prop-types';
import { Input, Select, Button } from 'antd';
import { SearchOutlined, SwapOutlined, FilterFilled } from '@ant-design/icons';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { setFilters, setSort } from '../../../../redux/actions/framl/landing';
import { statusLabel } from '../../../cm/utils/framlSummary';

const { Option } = Select;

const Bar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #e8eaf0;
  border-radius: 10px;
  margin-bottom: 14px;
  .ant-input-affix-wrapper {
    border-radius: 8px;
  }
  .ant-select-selector {
    border-radius: 8px !important;
  }
  label {
    font-size: 12px;
    color: #8a909c;
    font-weight: 500;
    margin: 0 0 0 4px;
  }
`;
const Spacer = styled.div`
  flex: 1;
`;
// Mock parity: funnel sits to the LEFT of the label with the default chevron kept
// on the right (AntD v4 Select has no prefix prop, so overlay + pad the selector).
const StatusFilter = styled.div`
  position: relative;
  display: inline-flex;
  .ant-select-selector {
    padding-left: 30px !important;
  }
`;
const LeadFilterIcon = styled(FilterFilled)`
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  color: #8a909c;
  font-size: 13px;
  pointer-events: none;
`;

// FIN-12894 — alphabetical after the "All" entry. Source has no severity semantics, so unlike
// the risk bands (which rank HIGH > GOOD > LOW via riskSeverityRank below) this is just a literal
// in the order it should render. The batch-2 fix added ordering to the risk dropdown only, which
// is why Risk Bands passed re-test and Source did not.
//
// The `value` strings are the BE contract read by handleSourceChange -> setFilters({ source }) ->
// currentRepo.listForQueue, so ONLY the order changes here.
// FIN-12916 — this control is labelled DOMAIN, matching the grid column it filters.
// The ticket's first row: the dropdown said "Source"/"All Sources" while the column it
// narrows is "Domain", and the option values themselves are domains (AML / Cross-Domain /
// Fraud), never sources. The query param stays `source` — the wire contract is unchanged,
// exactly as with riskBand for Alert Level.
const SOURCE_OPTIONS = [
  { label: 'All Domains', value: 'all', sources: [] },
  { label: 'AML', value: 'aml', sources: ['aml'] },
  { label: 'Cross-Domain', value: 'cross-domain', sources: ['cross-domain'] },
  { label: 'Fraud', value: 'fraud', sources: ['fraud'] }
];

// Fallback risk options — used when cmWorkFlowStatus.alertLevels is empty or unavailable.
// FIN-12894: explicit severity ordering High → Good → Low (mock parity), applied to the
// configured list too (see riskSeverityRank below) — the raw alertLevels arrive in
// insertion order.
const FALLBACK_RISK_OPTIONS = [
  { label: 'High', value: 'high' },
  { label: 'Good', value: 'good' },
  { label: 'Low', value: 'low' }
];

// FIN-12894 — severity rank for the configured list; unknown codes sink to the end
// in their configured order (stable sort).
const RISK_SEVERITY_ORDER = ['HIGH', 'GOOD', 'LOW'];
const riskSeverityRank = (code) => {
  const i = RISK_SEVERITY_ORDER.indexOf(String(code || '').toUpperCase());
  return i === -1 ? RISK_SEVERITY_ORDER.length : i;
};

// FIN-12916 — Status now offers the CM WORKFLOW statuses, not the assignment enum.
//
// The three fixed values that used to live here (ASSIGNED / PARTIALLY_ASSIGNED / UNASSIGNED)
// filtered `customer_framl_current.assignment_status`, a write-only legacy column that nothing
// populates — live keycloak-qa had all 7,193 customers reading UNASSIGNED, so two of the three
// options returned nothing and the third returned everything. The control could not filter.
//
// Re-pointing it rather than back-filling the column, because the column was also the wrong
// CONCEPT: the PRD ("FRAML Prevention - Case Manager FRAML View", page 3214147650) specifies
// Status as "All statuses as per CM workflow" and gives assignment its OWN column
// ("Assigned / Unassigned status and assignment count"), which the grid already renders. So
// assignment loses nothing by leaving this dropdown, and Status starts answering the question
// the PRD asks of it.
//
// Options come from the same configurable `cmWorkFlowStatus.workFlowStatusList` the row-expand
// panel labels its status pills from, through the same `statusLabel` helper — a tenant that
// renames a status sees the new name in both places, and the dropdown can never offer a status
// the data cannot contain. The VALUE stays the raw code, which is the BE contract.
const toStatusOption = (s, list) => ({
  label: statusLabel(s.code, list),
  value: s.code
});

const SORT_FIELD_SCORE = 'framlScore';
const SORT_FIELD_DATE = 'latestAlertDate';

function FramlFilterBar({
  dispatch,
  alertLevels,
  workFlowStatusList,
  storeSort,
  filters
}) {
  // Current sort is the store's sort (framlLanding.sort) — the field dropdown and the
  // direction toggle both read from it and dispatch setSort. No local state to drift.
  const sortField = storeSort?.sortField || SORT_FIELD_DATE;
  const sortDir = storeSort?.sortDir || 'desc';

  // FIN-12929: the bar is FULLY CONTROLLED from the store (framlLanding.filters) —
  // the search Input was uncontrolled and the three Selects used defaultValue, so
  // store-side filter changes (header-tile clicks, future resets) desynced from
  // what the controls displayed. Same pattern as the sort controls above.
  const activeFilters = filters || {};

  // Alphabetical by the DISPLAYED label (FIN-12894 asked for the dropdowns to be ordered),
  // which is the tenant's configured name, not the raw code.
  const statusOptions = (workFlowStatusList || [])
    .filter((s) => s && s.code)
    .map((s) => toStatusOption(s, workFlowStatusList))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Build risk options from configurable alertLevels (D3); fallback if empty/undefined
  const riskOptions =
    alertLevels && alertLevels.length > 0
      ? [...alertLevels]
          // FIN-12894: High → Good → Low regardless of the configured list's insertion order
          .sort((a, b) => riskSeverityRank(a.code) - riskSeverityRank(b.code))
          .map(({ code, displayName }) => ({
            label: displayName || code,
            value: code.toLowerCase()
          }))
      : FALLBACK_RISK_OPTIONS;

  const handleSearch = (e) => {
    dispatch(setFilters({ search: e.target.value }));
  };

  // FIN-11360: dispatch the CANONICAL contract keys (source/riskBand/sortField/sortDir),
  // matching the reducer shape + the BE query params. (The earlier sources[]/risk[]/field/dir
  // keys were silently ignored by the BE.)
  const handleSourceChange = (value) => {
    dispatch(setFilters({ source: value === 'all' ? '' : value }));
  };

  const handleRiskChange = (value) => {
    dispatch(setFilters({ riskBand: value === 'all' ? '' : value }));
  };

  const handleStatusChange = (value) => {
    dispatch(setFilters({ status: value === 'all' ? '' : value }));
  };

  return (
    <Bar>
      <Input
        placeholder="Search by ID, customer, typology, variant..."
        value={activeFilters.search ?? ''}
        onChange={handleSearch}
        aria-label="search"
        prefix={<SearchOutlined style={{ color: '#b0b6c0' }} />}
        style={{ width: 300 }}
      />
      <Select
        id="source-select"
        onChange={handleSourceChange}
        value={activeFilters.source || 'all'}
        aria-label="Domain"
        style={{ minWidth: 140 }}>
        {SOURCE_OPTIONS.map((o) => (
          <Option key={o.value} value={o.value}>
            {o.value === 'all' ? 'All Domains' : o.label}
          </Option>
        ))}
      </Select>
      <StatusFilter>
        <LeadFilterIcon />
        <Select
          id="status-select"
          onChange={handleStatusChange}
          value={activeFilters.status || 'all'}
          aria-label="Status"
          style={{ minWidth: 150 }}>
          <Option key="all" value="all">
            All Statuses
          </Option>
          {statusOptions.map((o) => (
            <Option key={o.value} value={o.value}>
              {o.label}
            </Option>
          ))}
        </Select>
      </StatusFilter>
      <Select
        id="risk-select"
        onChange={handleRiskChange}
        value={activeFilters.riskBand || 'all'}
        aria-label="Alert Level"
        style={{ minWidth: 130 }}>
        <Option key="all" value="all">
          All Alert Levels
        </Option>
        {riskOptions.map((o) => (
          <Option key={o.value} value={o.value}>
            {o.label}
          </Option>
        ))}
      </Select>
      <Spacer />
      <label htmlFor="sort-field-select">Sort</label>
      <Select
        id="sort-field-select"
        aria-label="Sort field"
        value={sortField}
        onChange={(field) => dispatch(setSort({ sortField: field, sortDir }))}
        style={{ width: 170 }}>
        {/* FIN-12916 — "Date", matching the grid column. The sort FIELD stays
            latestAlertDate; only the label the analyst reads changes. */}
        <Option value={SORT_FIELD_DATE}>Date</Option>
        <Option value={SORT_FIELD_SCORE}>FRAML Score</Option>
      </Select>
      <Button
        aria-label="Toggle sort direction"
        // FIN-13509 — the tooltip must name the ACTION the click performs, not the
        // current state: this loads at sortDir='desc' by default, so the old label
        // ("Descending") read as a static description rather than a control hint.
        title={sortDir === 'desc' ? 'Sort ascending' : 'Sort descending'}
        icon={<SwapOutlined rotate={90} />}
        onClick={() =>
          dispatch(
            setSort({ sortField, sortDir: sortDir === 'desc' ? 'asc' : 'desc' })
          )
        }
      />
    </Bar>
  );
}

FramlFilterBar.propTypes = {
  dispatch: PropTypes.func.isRequired,
  alertLevels: PropTypes.array,
  workFlowStatusList: PropTypes.array,
  storeSort: PropTypes.shape({
    sortField: PropTypes.string,
    sortDir: PropTypes.string
  }),
  // FIN-12929: canonical filter state (framlLanding.filters) — the bar renders it.
  filters: PropTypes.shape({
    search: PropTypes.string,
    source: PropTypes.string,
    riskBand: PropTypes.string,
    status: PropTypes.string
  })
};

const mapStateToProps = ({ cmWorkFlowStatus, framlLanding }) => ({
  alertLevels: cmWorkFlowStatus?.alertLevels,
  // FIN-12916 — the configurable CM workflow statuses now drive the Status dropdown.
  // FramlLandingPage already dispatches fetchWorkFlowStatusList on mount for the expand panel.
  workFlowStatusList: cmWorkFlowStatus?.workFlowStatusList,
  storeSort: framlLanding?.sort,
  filters: framlLanding?.filters
});

export default connect(mapStateToProps)(FramlFilterBar);
